import {
    getPriceString,
    useEcommerceTelemetry,
} from '@experiences/ecommerce';
import { useLocalization } from '@experiences/locales';
import { GlobalStyles } from '@experiences/theme';
import {
    UiSuspensefulOutlet,
    UiText,
} from '@experiences/ui-common';
import {
    formatDate,
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import * as RouteNames from '../../common/constants/RouteNames';
import {
    billingUrl,
    fetchPlanDetails,
    goToStripeCustomerPortalSession,
} from '../../services/licensing/BillingService';
import { accountLogicalName } from '../../store/selectors';
import { UiLicenseExpiredBanner } from '../common/UiLicenseExpiredBanner';
import { UiLicenseInGracePeriodBanner } from '../common/UiLicenseInGracePeriodBanner/index.default';
import { UiPanel } from '../common/UiPanel/UiPanel';
import { UiTrialPerSkuLicenseInGracePeriodBanners } from '../common/UiTrialPerSkuLicenseInGracePeriodBanners';

const useStyles = makeStyles(theme => ({
    ...GlobalStyles(theme),
    ...createStyles({
        managePlanContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        breadcrumbText: {
            fontSize: '18px',
            lineHeight: '24px',
            color: theme.palette.semantic.colorForeground,
            fontWeight: 600,
        },
        breadcrumbClickable: {
            cursor: 'pointer',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        title: {
            color: theme.palette.semantic.colorForeground,
            display: 'flex',
            alignItems: 'center',
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 'bold',
            marginTop: '24px',
            marginBottom: '12px',
        },
        subTitle: {
            color: theme.palette.semantic.colorForegroundDeEmp,
            marginBottom: '10px',
            display: 'flex',
            fontWeight: 600,
            fontSize: '14px',
        },
        detailContainer: {
            display: 'flex',
            flexDirection: 'row',
        },
        detailColumn: {
            display: 'flex',
            minWidth: '121px',
            lineHeight: '20px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            marginRight: '50px',
        },
        details: {
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '10px',
            fontWeight: 'normal',
            fontSize: '14px',
        },
        hyperLinkText: {
            fontWeight: 600,
            color: theme.palette.semantic.colorPrimary,
            '&:hover': {
                cursor: 'pointer',
                textDecoration: 'underline',
            },
        },
        link: { fontWeight: 600 },
        renewalSection: { marginTop: '25px' },
        planDetailSection: { marginTop: '5px' },
        updatePaymentButton: {
            marginTop: '10px',
            marginBottom: '10px',
            padding: '0px, 16px',
            width: '137px',
            fontSize: '14px',
        },
        titleContainer: {
            flexGrow: 1,
            marginTop: '10px',
        },
        headerText: {
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '24px',
            marginTop: '8px',
        },
        loading: {
            position: 'absolute',
            top: '50%',
            left: 'calc(50% + 100px)',
        },
        divider: {
            margin: '12px 0px 24px 0px',
            backgroundColor: theme.palette.semantic.colorBorderDeEmp,
        },
        salesCards: { display: 'flex' },
        card: {
            padding: '16px',
            paddingRight: '24px',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: 'none',
            borderRadius: '3px',
            borderWidth: '1px',
            borderColor: theme.palette.semantic.colorBorderDeEmp,
            marginRight: '16px',
        },
        cardTitle: {
            fontWeight: 600,
            lineHeight: '20px',
            marginBottom: '11px',
            color: theme.palette.semantic.colorForeground,
        },
        salesLink: { fontSize: '14px' },
    }),
}));

const EcommerceManagePlanComponent: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const getRoute = useRouteResolver();
    const navigate = useNavigateWithParams();

    const logEcommerceEvent = useEcommerceTelemetry();

    const language = useLocalization();
    const currentAccountName = useSelector(accountLogicalName);
    const {
        data: planDetails, isValidating: loading,
    } = useSWR(
        {
            url: `${billingUrl}/planDetails`,
            accountName: currentAccountName,
        },
        fetchPlanDetails,
    );
    const [ endDate, nextPayment, upcomingBillValue, currency ] = useMemo(
        () => [
            planDetails?.endDate,
            planDetails?.nextPayment,
            planDetails?.upcomingBill,
            planDetails?.currency,
        ],
        [ planDetails ],
    );

    return (
        <UiPanel
            licenseInGracePeriodBanner={<UiLicenseInGracePeriodBanner />}
            licenseExpiredBanner={<UiLicenseExpiredBanner />}
            trialPerSkuLicenseInGracePeriodBanners={<UiTrialPerSkuLicenseInGracePeriodBanners />}
            header={{
                title: (
                    <Breadcrumbs separator="/">
                        <span
                            className={clsx(classes.breadcrumbText, classes.breadcrumbClickable)}
                            onClick={() => navigate(getRoute(RouteNames.Licensing))}
                        >
                            {translate({ id: 'CLIENT_LICENSES' })}
                        </span>
                        <span
                            className={classes.breadcrumbText}
                            role="heading"
                            aria-level={1}>
                            {translate({ id: 'CLIENT_MANAGE_PLAN' })}
                        </span>
                    </Breadcrumbs>
                ),
                disableGutter: true,
            }}
        >
            {loading ? (
                <div className={classes.loading}>
                    <CircularProgress />
                </div>
            ) : (
                <div className={classes.managePlanContainer}>
                    <div className={classes.planDetailSection}>
                        <UiText className={classes.title}>
                            {translate({ id: 'CLIENT_PLAN_DETAIL' })}
                        </UiText>
                        <div
                            className={classes.detailContainer}
                            data-cy="plandetail-container">
                            <div className={classes.detailColumn}>
                                <UiText
                                    data-cy="client-plan-text"
                                    className={classes.subTitle}>
                                    {translate({ id: 'CLIENT_PLAN' })}
                                </UiText>
                                <UiText
                                    data-cy="client-license-redirect-text"
                                    className={classes.subTitle}>
                                    {translate({ id: 'CLIENT_LICENSES_INCLUDED' })}
                                </UiText>
                                {planDetails && (
                                    <>
                                        <UiText
                                            data-cy="client-plan-type-text"
                                            className={classes.subTitle}>
                                            {translate({ id: 'CLIENT_PLAN_TYPE' })}
                                        </UiText>
                                    </>
                                )}
                            </div>
                            <div className={classes.detailColumn}>
                                <UiText
                                    data-cy="client-plan-value"
                                    className={classes.details}>
                                    {!planDetails
                                        ? translate({ id: 'CLIENT_CUSTOM_PLAN' })
                                        : translate({ id: 'CLIENT_SKU_PACKAGE_PACK_CUSTOM' })}
                                </UiText>
                                <Link
                                    className={clsx(classes.details, classes.hyperLinkText)}
                                    data-cy="client-license-redirect-value"
                                    onClick={() => navigate(getRoute(RouteNames.Licensing))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {translate({ id: 'CLIENT_VIEW_LICENSES_PAGE' })}
                                </Link>
                                {planDetails && (
                                    <>
                                        <UiText
                                            data-cy="client-plan-type-value"
                                            className={classes.details}>
                                            {translate({ id: 'CLIENT_MONTHLY_PLAN' })}
                                        </UiText>
                                    </>
                                )}
                            </div>
                        </div>
                        {!planDetails && (
                            <UiText
                                data-cy="client-custom-plan-message"
                                className={classes.details}>
                                {translate({ id: 'CLIENT_CUSTOM_PLAN_MESSAGE' })}
                            </UiText>
                        )}
                    </div>

                    {planDetails && (
                        <>
                            <Button
                                className={classes.updatePaymentButton}
                                variant="outlined"
                                onClick={() => {
                                    goToStripeCustomerPortalSession(currentAccountName);
                                }}
                                data-cy="update-payment-info-button"
                            >
                                {translate({ id: 'CLIENT_MANAGE_BILLING_BUTTON' })}
                            </Button>
                            <UiText className={classes.details}>
                                {translate({ id: 'CLIENT_BUTTON_HINT' })}
                            </UiText>

                            <div
                                className={classes.renewalSection}
                                data-cy="renewal-section">
                                <UiText className={classes.title}>
                                    {translate({ id: 'CLIENT_PLAN_RENEWAL' })}
                                </UiText>
                                {nextPayment ? (
                                    <>
                                        <UiText className={classes.details}>
                                            {translate(
                                                { id: 'CLIENT_PLAN_RENEWAL_MESSAGE' },
                                                { 0: formatDate(endDate, language) },
                                            )}
                                        </UiText>
                                    </>
                                ) : (
                                    <UiText
                                        className={classes.details}
                                        data-cy="plan-end-message">
                                        {translate(
                                            { id: 'CLIENT_PLAN_END_MESSAGE' },
                                            { 0: formatDate(endDate, language) },
                                        )}
                                    </UiText>
                                )}
                                <div
                                    className={classes.detailContainer}
                                    data-cy="plandetail-container">
                                    <div className={classes.detailColumn}>
                                        <UiText
                                            data-cy="client-upcoming-bill-text"
                                            className={classes.subTitle}>
                                            {translate({ id: 'CLIENT_UPCOMING_BILL' })}
                                        </UiText>
                                    </div>
                                    <div className={classes.detailColumn}>
                                        {upcomingBillValue ? (
                                            <UiText
                                                data-cy="client-upcoming-bill-value"
                                                className={classes.details}>
                                                {currency && getPriceString(upcomingBillValue, currency, language)}
                                                {translate({ id: 'CLIENT_TAX' })}
                                            </UiText>
                                        ) : (
                                            <UiText
                                                data-cy="client-none-upcoming-bill"
                                                className={classes.details}>
                                                {translate({ id: 'CLIENT_NONE' })}
                                            </UiText>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    <Divider className={classes.divider} />

                    <div className={classes.salesCards}>
                        <Card
                            variant="outlined"
                            className={classes.card}
                            data-cy="manageplan-support-section">
                            <UiText className={classes.cardTitle}>
                                {translate({ id: 'CLIENT_SUPPORT' })}
                            </UiText>
                            <UiText className={classes.details}>
                                {translate({ id: 'CLIENT_BILLING_QUESTIONS' })}
                            </UiText>
                            <UiText className={classes.link}>
                                <FormattedMessage
                                    id="CLIENT_CONTACT_UI_PATH_AT"
                                    values={{
                                        a: (chunk: React.ReactNode[]) => (
                                            <a
                                                className={classes.a}
                                                onClick={() => {
                                                    logEcommerceEvent('ManagePlan.ContactSupport');
                                                }}
                                                href="mailto:cloud.billing@uipath.com"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {chunk}
                                            </a>
                                        ),
                                    }}
                                />
                            </UiText>
                        </Card>
                        <Card
                            variant="outlined"
                            className={classes.card}
                            data-cy="manageplan-services-section">
                            <UiText className={classes.cardTitle}>
                                {translate({ id: 'CLIENT_NEED_MORE_SERVICES' })}
                            </UiText>
                            <UiText className={classes.details}>
                                {translate({ id: 'CLIENT_GET_FLEXIBLE_OFFERINGS' })}
                            </UiText>
                            <Link
                                className={classes.salesLink}
                                onClick={() => {
                                    logEcommerceEvent('ManagePlan.ContactSales');
                                }}
                                href={
                                    language === 'en'
                                        ? 'https://www.uipath.com/company/contact-us'
                                        : `https://www.uipath.com/${language}/company/contact-us`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translate({ id: 'CLIENT_CONTACT_SALES' })}
                            </Link>
                        </Card>
                    </div>
                </div>
            )}
        </UiPanel>
    );
};

export default () => (
    <>
        <EcommerceManagePlanComponent />
        <UiSuspensefulOutlet />
    </>
);
